"use client";

/**
 * Client side of the "New" pills on menu items, plus the roadmap steps
 * on the hub. Both come from GET /api/announcements/active, fetched
 * once per page load per locale (a module-level cache every caller
 * shares). The browser's own "seen" set lives in localStorage
 * ("menzil.ann.seen", item ids).
 *
 * WHEN THE "NEW" PILL SHOWS
 * An item of a PUBLISHED release shows a pill when, in the admin app,
 * it has a "New marker path" (badge_href, e.g. "/calculate/mortgage"),
 * its marker end date ("until") has not passed (a bare date counts
 * through the end of that day; no date means no expiry) AND its release
 * went out less than NEW_MARK_DAYS (7) ago (``since``; the quiet UI,
 * 2026-10-29: a mark is news for a week, then part of the site). The
 * API only returns markers of published releases; isLive() and
 * isFreshMark() re-check the dates so a cached answer cannot keep an
 * expired pill.
 *
 * Where it shows:
 * - Desktop (sm and up): in the header, the menu group that holds the
 *   matching item gets a small grey dot beside its label, and
 *   the item inside that group's dropdown gets the "New" pill. Hover,
 *   focus or click the group to see it.
 * - Phones: the item in the More sheet gets the "New" pill. The bottom
 *   tab bar never carries one.
 * Which menu item matches: the item whose path equals the marker path;
 * if no menu item has that exact path, a marker on a section root
 * (e.g. "/trends") lights the items under it, and a marker deeper than
 * any menu item (e.g. "/trends/investor/x") lights the closest menu
 * item above it ("/trends/investor").
 *
 * When it goes away (per browser, signed in or not): as soon as the
 * marked page (or a page under it) is visited by any route
 * (AnnouncementsSeenOnVisit in the locale layout), or the menu item is
 * clicked, or the end date passes.
 *
 * The release-level dot on the header "What's new" button and the
 * one-time corner card are separate: see lib/use-releases.ts.
 *
 * Everything degrades to nothing: no API, no storage, no JS errors,
 * just no markers. A seen marker disappears immediately in every
 * subscriber (one external store, useSyncExternalStore).
 */
import { useCallback, useEffect, useMemo, useSyncExternalStore } from "react";
import { API_BASE } from "@/lib/api";
import { useLocale } from "@/lib/locale-context";
import { NAV_GROUPS } from "@/lib/nav-groups";
import { useAuth } from "@/lib/auth-context";
import {
  isFreshMark,
  isLive,
  pathUnder,
  plainPath,
  type ActiveAnnouncements,
  type AnnouncementBadge,
  type AnnouncementRoadmapItem,
} from "@/lib/announcements";

const SEEN_KEY = "menzil.ann.seen";
const EMPTY: ActiveAnnouncements = { badges: [], roadmap: [] };

interface Snapshot {
  active: ActiveAnnouncements;
  seen: ReadonlySet<string>;
}

let snapshot: Snapshot = { active: EMPTY, seen: new Set() };
const SERVER_SNAPSHOT: Snapshot = snapshot;
let seenLoaded = false;
let loadedLocale: string | null = null;
const listeners = new Set<() => void>();

function emit(next: Partial<Snapshot>) {
  snapshot = { ...snapshot, ...next };
  listeners.forEach((l) => l());
}

function readSeen(): Set<string> {
  try {
    const raw = localStorage.getItem(SEEN_KEY);
    const arr = raw ? (JSON.parse(raw) as unknown) : [];
    return new Set(Array.isArray(arr) ? arr.map(String) : []);
  } catch {
    return new Set();
  }
}

function writeSeen(seen: ReadonlySet<string>) {
  try {
    localStorage.setItem(SEEN_KEY, JSON.stringify([...seen].slice(-200)));
  } catch { /* storage unavailable: seen lasts for this page load */ }
}

function sanitize(data: unknown): ActiveAnnouncements {
  const d = (data ?? {}) as Partial<ActiveAnnouncements>;
  const badges = Array.isArray(d.badges)
    ? d.badges.filter((b): b is AnnouncementBadge => !!b && typeof b.badge_href === "string" && b.badge_href.startsWith("/"))
    : [];
  const roadmap = Array.isArray(d.roadmap)
    ? d.roadmap
        .filter((r): r is AnnouncementRoadmapItem => !!r && typeof r.title === "string" && typeof r.slug === "string")
        .map((r) => ({ ...r, intents: Array.isArray(r.intents) ? r.intents : [] }))
    : [];
  return { badges, roadmap };
}

function load(locale: string) {
  if (!seenLoaded) {
    seenLoaded = true;
    emit({ seen: readSeen() });
  }
  if (loadedLocale === locale) return;
  loadedLocale = locale;
  fetch(`${API_BASE}/announcements/active?locale=${encodeURIComponent(locale)}`, {
    credentials: "omit",
  })
    .then((r) => (r.ok ? r.json() : null))
    .then((data) => { if (loadedLocale === locale) emit({ active: data ? sanitize(data) : EMPTY }); })
    .catch(() => { if (loadedLocale === locale) emit({ active: EMPTY }); });
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  return () => { listeners.delete(cb); };
}

/** Mark announcement ids seen (stored, and gone from every marker). */
export function markAnnouncementsSeen(ids: Array<number | string>) {
  if (!ids.length) return;
  const seen = new Set(snapshot.seen);
  let changed = false;
  for (const id of ids) {
    const k = String(id);
    if (!seen.has(k)) { seen.add(k); changed = true; }
  }
  if (!changed) return;
  writeSeen(seen);
  emit({ seen });
}

// Every href the menus list, so a badge on "/calculate" lights only the
// "/calculate" row and not every calculator under it.
const ALL_NAV_HREFS = new Set(
  NAV_GROUPS.flatMap((g) => [g.href, ...g.sections.flatMap((s) => s.items.map((i) => i.href))]).map(plainPath),
);

/** The deepest menu href the path sits under (never the site root). */
function closestNavAncestor(path: string): string | null {
  let best: string | null = null;
  for (const h of ALL_NAV_HREFS) {
    if (h === "/" || !pathUnder(path, h)) continue;
    if (!best || h.length > best.length) best = h;
  }
  return best;
}

function badgeHitsNav(navHref: string, badgeHref: string): boolean {
  const nav = plainPath(navHref);
  const badge = plainPath(badgeHref);
  if (nav === badge) return true;
  // The menus carry the badge path itself: that exact row owns it.
  if (ALL_NAV_HREFS.has(badge)) return false;
  // The badge on a section root above menu items lights those items.
  if (pathUnder(nav, badge)) return true;
  // The badge deeper than any menu item lights the closest one above.
  return closestNavAncestor(badge) === nav;
}

export interface UseAnnouncements {
  /** Live, unseen badges. */
  badges: AnnouncementBadge[];
  /** Live roadmap items (the caller filters by intent). */
  roadmap: AnnouncementRoadmapItem[];
  /** Unseen live badges that put a "New" marker on this nav href. */
  newFor: (href: string) => AnnouncementBadge[];
  /** Mark the badges on this nav href seen (the item was clicked). */
  seeHref: (href: string) => void;
}

export function useAnnouncements(): UseAnnouncements {
  const { locale } = useLocale();
  const snap = useSyncExternalStore(subscribe, () => snapshot, () => SERVER_SNAPSHOT);
  useEffect(() => { load(locale); }, [locale]);

  const badges = useMemo(
    () => snap.active.badges.filter((b) => isLive(b.until) && isFreshMark(b.since) && !snap.seen.has(String(b.id))),
    [snap],
  );
  const roadmap = useMemo(() => snap.active.roadmap.filter((r) => isLive(r.until)), [snap]);

  const newFor = useCallback(
    (href: string) => badges.filter((b) => badgeHitsNav(href, b.badge_href)),
    [badges],
  );
  const seeHref = useCallback(
    (href: string) => markAnnouncementsSeen(badges.filter((b) => badgeHitsNav(href, b.badge_href)).map((b) => b.id)),
    [badges],
  );
  return { badges, roadmap, newFor, seeHref };
}

/**
 * Visiting a page a badge points at counts as seeing it. Mounted once
 * in the locale layout, so it runs on every page (including the ones
 * without SharedHeader).
 */
export function useSeenOnVisit(pathname: string | null) {
  const { badges } = useAnnouncements();
  useEffect(() => {
    if (!pathname || badges.length === 0) return;
    const hit = badges.filter((b) => pathUnder(pathname, b.badge_href));
    if (hit.length) markAnnouncementsSeen(hit.map((b) => b.id));
  }, [pathname, badges]);
}


/** "New here" marks (the nav dots and pills, the release dot) are for
 *  people who know the site: everything is new to a visitor who is signed
 *  out or on their first day (owner, 2026-10-17). */
export function useMarksNew(): boolean {
  const { user } = useAuth();
  return !!user?.created_at && Date.now() - Date.parse(user.created_at) > 24 * 3600 * 1000;
}
